import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_MENU_ARGS,
  INPUT_TRIGGER_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { HIDDEN_SELECTOR } from '../../../../utils/keyboard-nav-support';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import MultiselectClearFilter from '../multiselect-clear-filter.gjs';
import SearchInput from './field.gjs';
import Super from '../../core/base.gjs';
import styles from './trigger.module.scss';

const METADATA = Metadata.define({
  description: 'Trigger input and controls for the search component',
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'ariaLabel', defaultValue: 'Search' }]),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'error', 'id', 'open']),
    pick(INPUT_TRIGGER_ARGS, [{ name: 'menuOpenThreshold', required: true }]),
    pick(INPUT_MENU_ARGS, ['multiselect']),
    pick(ACTION_ARGS, [{ name: 'onClose', required: true }, 'onInput']),
    pick(INPUT_FORM_ARGS, ['placeholder', 'required', 'value']),
  ],
  meta: {
    clearInput: Arg.action({
      description: 'Clears the input value (wired by the parent search component)',
      private: true,
    }),
    clearSelection: Arg.action({
      description:
        'Clears the current multiselect selection (wired by the parent search component)',
      private: true,
    }),
    closeMenu: Arg.action({
      description: 'Closes the search menu (wired by the parent search component)',
      private: true,
    }),
    onClearInput: Arg.action({ description: 'Action triggered when the clear button is clicked' }),
    openMenu: Arg.action({ description: 'Action to open the search menu' }).required(),
    registerClearFilterComponent: Arg.action({
      description: 'Action to register the clear filter component',
    }),
    searchIcon: Arg.string({ description: 'Overrides the default search icon' }),
    selected: Arg.object({ description: 'Currently selected option(s)' }),
    unregisterClearFilterComponent: Arg.action({
      description: 'Action to unregister the clear filter component',
    }),
  },
  name: 'Search Trigger',
});

export default class SpdsInputSearchTrigger extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-input-search-trigger';
  classesToMap = ['disabled', 'error'];
  clearBtnSelector = 'spds-input-search-clear-btn';
  elementsToRegister = [
    { name: 'clearButton', selector: `.${this.clearBtnSelector}` },
    { name: 'trigger', selector: 'input' },
  ];
  placeholderDefault = 'Placeholder';
  styles = styles;

  @tracked clearButton;
  @tracked clearFilterComponent;

  get ariaLabel() {
    return this.argOrDefault('ariaLabel');
  }

  get clearButtonClasses() {
    let classes = [this.clearBtnSelector];

    if (this.hideClearButton) classes.push(HIDDEN_SELECTOR);

    return classes.join(' ');
  }

  get clearButtonId() {
    return this.composeSubcomponentId('clear-button');
  }

  get clearFilterId() {
    return this.composeSubcomponentId('multiselect-clear');
  }

  get containerId() {
    return this.composeSubcomponentId('trigger');
  }

  get hasSelectedOption() {
    let { multiselect, selected } = this.args;

    return !multiselect && selected;
  }

  get hideClearButton() {
    return this.args.value?.length <= 0;
  }

  get placeholder() {
    return this.args.placeholder ?? this.placeholderDefault;
  }

  get shouldShowFilter() {
    let { multiselect, selected } = this.args;

    return multiselect && Array.isArray(selected) && selected.length > 0;
  }

  onInput = evt => {
    if (evt.newValue) {
      evt.target.value = evt.newValue;
    }

    this.toggleMenu();
  };

  toggleMenu = () => {
    let { menuOpenThreshold } = this.args;

    if (this.trigger.value.length >= menuOpenThreshold && !this.args.open) {
      this.args.openMenu();
    } else if (this.trigger.value.length < menuOpenThreshold && this.args.open) {
      this.args.closeMenu();
    }
  };

  <template>
    <SearchInput
      @gap="10px"
      @id={{this.containerId}}
      class={{this.classes}}
      data-test-spds-input-search-input-container
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
      as |si|
    >
      {{#if this.shouldShowFilter}}
        <MultiselectClearFilter
          @disabled={{@disabled}}
          @clearSelection={{@clearSelection}}
          @id={{this.clearFilterId}}
          @registerComponent={{fn this.registerComponent "clearFilterComponent"}}
          @selected={{@selected}}
          @unregisterComponent={{fn this.unregisterComponent "clearFilterComponent"}}
          data-test-spds-input-search-multiselect-clear
        />
      {{else}}
        <si.Icon @class="spds-input-search-icon" @name={{or @searchIcon "search"}} />
      {{/if}}

      <si.Input
        @ariaLabel={{this.ariaLabel}}
        @disabled={{@disabled}}
        @id={{this.id}}
        @onInput={{@onInput}}
        @placeholder={{this.placeholder}}
        @required={{@required}}
        @value={{@value}}
        class="spds-input-search-input"
        data-test-spds-input-search-input
        {{on "click" this.toggleMenu}}
        {{on "input" this.onInput}}
      />

      <si.ClearButton
        @id={{this.clearButtonId}}
        class={{this.clearButtonClasses}}
        data-test-spds-input-search-clear-button
        {{on "click" @clearInput}}
      />
    </SearchInput>
  </template>
}

export const SEARCH_TRIGGER_METADATA = SpdsInputSearchTrigger.metadata;
